/**
 * The converter for time and distance.
 *
 * @module Map
 * @class unitFormatConverter
 */
define('unitFormatConverter/unitFormatConverter',[], function() {
    'use strict';

    var _currentUnitsScheme = 'metric'; // could be 'metric' or 'imperial'
    var allowedUnitSchemes = ['metric', 'imperial'];

    function withLeadingZero(value) {
        return value < 10 ? '0' + value : value;
    }

    function imperialDistance(distanceMeters) {
        var yards = 1.0936133 * distanceMeters;
        var miles = 0.000621371192 * distanceMeters;
        return miles < 1 ? Math.round(yards) + ' yd' : Math.round(miles * 10) / 10 + ' mi';
    }

    function metricDistance(distanceMeters) {
        var distance = Math.round(distanceMeters);

        if (distance >= 1000) {
            return Math.round(distance / 100) / 10 + ' km';
        }
        return distance + ' m';
    }

    return {
        /**
         * Converts given time in seconds to the format of 'HH h MM min' or 'MM min SS s'.
         * HH - hours MM - minutes SS - seconds.
         *
         * @method formatTime
         * @param {Integer} [seconds] Number of seconds to be formatted.
         * @return {String} Returns a formatted time.
         *
         * @example
         * ```javascript
         *
         *  unitFormatConverter.formatTime(3600);  // gives 1 h 0 min
         * ```
         */
        formatTime: function(seconds) {
            var secondsNum, hours, minutes, secondsFormatted, minutesFormatted;
            if (seconds === undefined) {
                return undefined;
            }
            secondsNum = parseInt(seconds, 10),
                hours = Math.floor(secondsNum / 3600),
                minutes = Math.floor((secondsNum - hours * 3600) / 60);
            if (secondsNum < 60) {
                return secondsNum + ' s';
            }
            secondsNum %= 60;
            secondsFormatted = withLeadingZero(secondsNum);
            minutes %= 60;
            minutesFormatted = withLeadingZero(minutes);
            if (hours > 0) {
                return hours + ':' + minutesFormatted + ' h';
            }
            return minutes + ':' + secondsFormatted + ' min';
        },

        /**
         * Converts a date in the machine format to a more human readable form.
         * Returns a formatted string created from the given date in UTC format.
         *
         * @method formatHourAndMinute
         * @param {String} [dateInMs] Date and time specified in the RFC-3339 format with an optional time zone offset.
         * @return {String} Method returns formatted time of hours and minutes.
         *
         * @example
         * ```javascript
         *
         *  unitFormatConverter.formatHourAndMinute('2016-03-01T15:45:58+01:00') // gives '3:45'
         * ```
         */
        formatHourAndMinute: function(dateInMs) {
            var date = new Date(dateInMs);
            var hour = date.getHours(),
                minute = date.getMinutes();
            return hour + ':' + withLeadingZero(minute);
        },

        /**
         * Converts distance in meters to a proper display format in SI or imperial units.
         *
         * @method formatDistance
         * @param {Integer} [distanceMeters] A distance in meters.
         * @param {Boolean} [imperial=false] Use imperial units. If not provided then default units scheme is used.
         * @return {String} Returns a formatted distance.
         */
        formatDistance: function(distanceMeters, imperial) {
            if (distanceMeters === undefined) {
                return undefined;
            }
            if (imperial || _currentUnitsScheme === 'imperial') {
                return imperialDistance(distanceMeters);
            } else {
                return metricDistance(distanceMeters);
            }
        },

        /**
         * Gets/Sets new units scheme: 'imperial' or 'metric'
         *
         * @method unitsScheme
         * @param {String} [newScheme] 'imperial' or 'metric'. If not provided then
         *        this method only returns current units scheme.
         * @return {String} Current units scheme. 'metric' is set by default.
         */
        unitsScheme: function(newScheme) {
            if (newScheme) {
                if (allowedUnitSchemes.indexOf(newScheme) > -1) {
                    _currentUnitsScheme = newScheme;
                } else {
                    console.warn('"' + newScheme + '" is unsupported. Allowed values are ' + allowedUnitSchemes);
                }
            }

            return _currentUnitsScheme;
        }
    };
});

